import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Layers, ChevronDown, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../utils';
import {
    nextLocalId,
    currencyFormat,
    minForTier,
    validateTiers,
} from './workflowValidation';

const emptyApprover = () => ({ id: nextLocalId(), approverKind: 'USER', userId: '', groupId: '', approvalType: 'REVIEWER' });
const emptyStep = () => ({ id: nextLocalId(), requiredApprovals: 1, approvers: [emptyApprover()] });

const APPROVAL_TYPE_TOOLTIPS = {
    REVIEWER: 'Can approve this step without an OTP code.',
    AUTHORISER: 'Must provide a valid OTP code to approve this step.',
};

function ApprovalTypeSelect({ value, disabled, onValueChange }) {
    return (
        <div className="flex items-center gap-1.5">
            <Select
                value={value || 'REVIEWER'}
                disabled={disabled}
                onValueChange={onValueChange}
            >
                <SelectTrigger className="w-40">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectItem value="REVIEWER">Reviewer</SelectItem>
                        <SelectItem value="AUTHORISER">Authoriser</SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Reviewer and Authoriser roles"
                    >
                        <Info className="h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <div className="flex flex-col gap-1.5 text-xs">
                        <p className="mb-0">
                            <span className="font-medium">Reviewer</span> — {APPROVAL_TYPE_TOOLTIPS.REVIEWER}
                        </p>
                        <p className="mb-0">
                            <span className="font-medium">Authoriser</span> — {APPROVAL_TYPE_TOOLTIPS.AUTHORISER}
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </div>
    );
}

export default function WorkflowBuilder({ tiers, setTiers, users, groups, disabled }) {

    const [expandedTiers, setExpandedTiers] = useState({});
    const [expandedSteps, setExpandedSteps] = useState({});

    const { errors, warnings } = useMemo(
        () => validateTiers(tiers, users, groups),
        [tiers, users, groups]
    );

    // The final step of every tier must use AUTHORISER approvers only. Force that role on
    // final-step approvers so the locked UI, validation and saved definition stay in sync.
    useEffect(() => {
        if (disabled) return;
        let changed = false;
        const normalized = tiers.map((tier) => {
            const lastStepIndex = tier.steps.length - 1;
            return {
                ...tier,
                steps: tier.steps.map((step, stepIndex) => {
                    if (stepIndex !== lastStepIndex) return step;
                    let stepChanged = false;
                    const approvers = step.approvers.map((ap) => {
                        if (ap.approvalType === 'AUTHORISER') return ap;
                        stepChanged = true;
                        return { ...ap, approvalType: 'AUTHORISER' };
                    });
                    if (!stepChanged) return step;
                    changed = true;
                    return { ...step, approvers };
                }),
            };
        });
        if (changed) setTiers(normalized);
    }, [tiers, setTiers, disabled]);

    const updateTier = (tierIndex, patch) => {
        setTiers((prev) => prev.map((t, i) => (i === tierIndex ? { ...t, ...patch } : t)));
    };

    const addTier = () => {
        setTiers((prev) => {
            const next = [...prev];
            // The current last tier gains a numeric upper bound; the new tier becomes unbounded.
            const last = next[next.length - 1];
            const lastMin = minForTier(next, next.length - 1);
            next[next.length - 1] = {
                ...last,
                maxAmountExclusive: last.maxAmountExclusive === null ? lastMin : last.maxAmountExclusive,
            };
            next.push({ id: nextLocalId(), maxAmountExclusive: null, steps: [emptyStep()] });
            return next;
        });
    };

    const removeTier = (tierIndex) => {
        setTiers((prev) => {
            if (prev.length === 1) return prev;
            const next = prev.filter((_, i) => i !== tierIndex);
            // Ensure the (new) last tier is unbounded.
            next[next.length - 1] = { ...next[next.length - 1], maxAmountExclusive: null };
            return next;
        });
    };

    const addStep = (tierIndex) => {
        setTiers((prev) => prev.map((t, i) => (
            i === tierIndex ? { ...t, steps: [...t.steps, emptyStep()] } : t
        )));
    };

    const removeStep = (tierIndex, stepIndex) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            if (t.steps.length === 1) return t;
            return { ...t, steps: t.steps.filter((_, s) => s !== stepIndex) };
        }));
    };

    const moveStep = (tierIndex, stepIndex, direction) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            const target = stepIndex + direction;
            if (target < 0 || target >= t.steps.length) return t;
            const steps = [...t.steps];
            [steps[stepIndex], steps[target]] = [steps[target], steps[stepIndex]];
            return { ...t, steps };
        }));
    };

    const updateStep = (tierIndex, stepIndex, patch) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            return { ...t, steps: t.steps.map((s, si) => (si === stepIndex ? { ...s, ...patch } : s)) };
        }));
    };

    const addApprover = (tierIndex, stepIndex) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            return {
                ...t,
                steps: t.steps.map((s, si) => (
                    si === stepIndex ? { ...s, approvers: [...s.approvers, emptyApprover()] } : s
                )),
            };
        }));
    };

    const removeApprover = (tierIndex, stepIndex, approverIndex) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            return {
                ...t,
                steps: t.steps.map((s, si) => {
                    if (si !== stepIndex) return s;
                    if (s.approvers.length === 1) return s;
                    return { ...s, approvers: s.approvers.filter((_, ai) => ai !== approverIndex) };
                }),
            };
        }));
    };

    const updateApprover = (tierIndex, stepIndex, approverIndex, patch) => {
        setTiers((prev) => prev.map((t, i) => {
            if (i !== tierIndex) return t;
            return {
                ...t,
                steps: t.steps.map((s, si) => {
                    if (si !== stepIndex) return s;
                    return {
                        ...s,
                        approvers: s.approvers.map((a, ai) => (ai === approverIndex ? { ...a, ...patch } : a)),
                    };
                }),
            };
        }));
    };

    const toggleTier = (tierId) => {
        setExpandedTiers((prev) => ({ ...prev, [tierId]: !prev[tierId] }));
    };

    const toggleStep = (stepId) => {
        setExpandedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
    };

    return (
        <TooltipProvider>
        <div className="flex flex-col gap-4">

            {(errors.length > 0 || warnings.length > 0) && (
                <div className="flex flex-col gap-2">
                    {errors.length > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3">
                            <p className="text-sm font-semibold text-red-700 mb-1">Resolve before publishing:</p>
                            <ul className="list-disc pl-5 text-sm text-red-700 mb-0">
                                {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                            </ul>
                        </div>
                    )}
                    {warnings.length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-4 h-4 text-amber-700" />
                                <p className="text-sm font-semibold text-amber-800 mb-0">Warnings (verified at publish):</p>
                            </div>
                            <ul className="list-disc pl-5 text-sm text-amber-800 mb-0">
                                {warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {tiers.map((tier, tierIndex) => {
                const isSingleTier = tiers.length === 1;
                const isLast = tierIndex === tiers.length - 1;
                const min = minForTier(tiers, tierIndex);
                const isTierCollapsed = !expandedTiers[tier.id];
                return (
                    <Card key={tier.id} className="border border-slate-200">
                        <CardHeader className={cn('pb-3', isTierCollapsed && 'pb-6')}>
                            <div className="flex items-center justify-between gap-2">
                                <button
                                    type="button"
                                    onClick={() => toggleTier(tier.id)}
                                    className="flex flex-1 min-w-0 items-center gap-2 text-left hover:opacity-80 transition-opacity"
                                >
                                    <ChevronDown
                                        className={cn(
                                            'h-4 w-4 shrink-0 text-slate-500 transition-transform',
                                            isTierCollapsed && '-rotate-90'
                                        )}
                                    />
                                    <Layers className="w-4 h-4 text-yavrio-blue shrink-0" />
                                    <h4 className="my-0 font-semibold">Tier {tierIndex + 1}</h4>
                                    <Badge variant="info">
                                        {isSingleTier
                                            ? 'All values'
                                            : `${currencyFormat(min)} – ${isLast ? 'and above' : currencyFormat(tier.maxAmountExclusive)}`}
                                    </Badge>
                                    {isTierCollapsed && (
                                        <span className="text-xs text-slate-400 truncate">
                                            {tier.steps.length} step{tier.steps.length === 1 ? '' : 's'}
                                        </span>
                                    )}
                                </button>
                                {!disabled && tiers.length > 1 && (
                                    <Button variant="ghost" className="p-2 text-red-400 shrink-0" onClick={() => removeTier(tierIndex)} title="Remove tier">
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        {!isTierCollapsed && (
                        <CardContent className="pb-8">
                            {!isSingleTier && (
                            <div className="flex flex-wrap items-end gap-4 mb-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-gray-500">Min (inclusive)</label>
                                    <Input className="w-40 bg-slate-50" value={currencyFormat(min)} disabled />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-gray-500">Max (exclusive)</label>
                                    {isLast ? (
                                        <Input className="w-40 bg-slate-50" value="No upper bound" disabled />
                                    ) : (
                                        <Input
                                            type="number"
                                            className="w-40"
                                            value={tier.maxAmountExclusive ?? ''}
                                            disabled={disabled}
                                            onChange={(e) => updateTier(tierIndex, {
                                                maxAmountExclusive: e.target.value === '' ? '' : Number(e.target.value),
                                            })}
                                        />
                                    )}
                                </div>
                            </div>
                            )}

                            <div className="flex flex-col gap-3">
                                {tier.steps.map((step, stepIndex) => {
                                    const isFinalStep = stepIndex === tier.steps.length - 1;
                                    const isStepCollapsed = !expandedSteps[step.id];
                                    const hasUsersAndGroups = step.approvers.some((ap) => ap.approverKind === 'USER' && ap.userId)
                                        && step.approvers.some((ap) => ap.approverKind === 'GROUP' && ap.groupId);
                                    return (
                                        <div key={step.id} className="rounded-md border border-slate-200 bg-slate-50/50">
                                            <div className="flex items-center justify-between gap-2 p-4 pb-3">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleStep(step.id)}
                                                    className="flex flex-1 min-w-0 items-center gap-2 text-left hover:opacity-80 transition-opacity"
                                                >
                                                    <ChevronDown
                                                        className={cn(
                                                            'h-4 w-4 shrink-0 text-slate-500 transition-transform',
                                                            isStepCollapsed && '-rotate-90'
                                                        )}
                                                    />
                                                    <Badge variant="secondary">Step {stepIndex + 1}</Badge>
                                                    {isFinalStep && <Badge variant="success">Final · Authorisers only</Badge>}
                                                    {isStepCollapsed && (
                                                        <span className="text-xs text-slate-400 truncate">
                                                            {step.requiredApprovals} distinct approval{Number(step.requiredApprovals) === 1 ? '' : 's'} required
                                                        </span>
                                                    )}
                                                </button>
                                                {!disabled && (
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <Button variant="ghost" className="p-2" disabled={stepIndex === 0} onClick={() => moveStep(tierIndex, stepIndex, -1)} title="Move up">
                                                            <ArrowUp className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" className="p-2" disabled={stepIndex === tier.steps.length - 1} onClick={() => moveStep(tierIndex, stepIndex, 1)} title="Move down">
                                                            <ArrowDown className="h-4 w-4" />
                                                        </Button>
                                                        {tier.steps.length > 1 && (
                                                            <Button variant="ghost" className="p-2 text-red-400" onClick={() => removeStep(tierIndex, stepIndex)} title="Remove step">
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {hasUsersAndGroups && (
                                                <p className="px-4 pb-3 mb-0 text-sm text-gray-600">
                                                    {step.requiredApprovals > 0
                                                        ? `Any ${step.requiredApprovals} distinct eligible user${Number(step.requiredApprovals) === 1 ? '' : 's'} from the selected users and groups can approve.`
                                                        : 'Choose how many distinct eligible users from the selected users and groups must approve.'}{' '}
                                                    Adding a named user alongside a group does not by itself make that user mandatory, but the required approval count may make their approval necessary.
                                                </p>
                                            )}

                                            {!isStepCollapsed && (
                                            <div className="px-4 pb-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                <label className="text-sm text-gray-600">Required approvals</label>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    className="w-20"
                                                    value={step.requiredApprovals}
                                                    disabled={disabled}
                                                    onChange={(e) => updateStep(tierIndex, stepIndex, {
                                                        requiredApprovals: e.target.value === '' ? '' : Math.max(1, Number(e.target.value)),
                                                    })}
                                                />
                                                <span className="text-xs text-gray-400">distinct approval(s) to clear this step</span>
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {step.approvers.map((approver, approverIndex) => {
                                                    const selectedUserIdsInStep = step.approvers
                                                        .filter((ap, i) => ap.approverKind === 'USER' && ap.userId && i !== approverIndex)
                                                        .map((ap) => Number(ap.userId));
                                                    const availableUsers = users.filter(
                                                        (u) => !selectedUserIdsInStep.includes(u.id) || Number(approver.userId) === u.id
                                                    );

                                                    return (
                                                    <div key={approver.id} className="flex flex-wrap items-center gap-2">
                                                        <Select
                                                            value={approver.approverKind}
                                                            disabled={disabled}
                                                            onValueChange={(value) => updateApprover(tierIndex, stepIndex, approverIndex, {
                                                                approverKind: value, userId: '', groupId: '',
                                                            })}
                                                        >
                                                            <SelectTrigger className="w-32">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectGroup>
                                                                    <SelectItem value="USER">User</SelectItem>
                                                                    <SelectItem value="GROUP">Group</SelectItem>
                                                                </SelectGroup>
                                                            </SelectContent>
                                                        </Select>

                                                        {approver.approverKind === 'USER' ? (
                                                            <Select
                                                                value={approver.userId ? String(approver.userId) : ''}
                                                                disabled={disabled}
                                                                onValueChange={(value) => updateApprover(tierIndex, stepIndex, approverIndex, { userId: Number(value) })}
                                                            >
                                                                <SelectTrigger className="w-64 text-left">
                                                                    <SelectValue placeholder="Select a user" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectGroup>
                                                                        {availableUsers.map((u) => (
                                                                            <SelectItem key={u.id} value={String(u.id)}>
                                                                                {u.name}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectGroup>
                                                                </SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <Select
                                                                value={approver.groupId ? String(approver.groupId) : ''}
                                                                disabled={disabled}
                                                                onValueChange={(value) => updateApprover(tierIndex, stepIndex, approverIndex, { groupId: Number(value) })}
                                                            >
                                                                <SelectTrigger className="w-64">
                                                                    <SelectValue placeholder="Select a group" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectGroup>
                                                                        {groups.length === 0 && (
                                                                            <div className="px-2 py-1.5 text-sm text-gray-400">No groups available</div>
                                                                        )}
                                                                        {groups.map((g) => (
                                                                            <SelectItem key={g.id} value={String(g.id)}>
                                                                                {g.name} ({g.members?.length || 0})
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectGroup>
                                                                </SelectContent>
                                                            </Select>
                                                        )}

                                                        <ApprovalTypeSelect
                                                            value={isFinalStep ? 'AUTHORISER' : approver.approvalType}
                                                            disabled={disabled || isFinalStep}
                                                            onValueChange={(value) => updateApprover(tierIndex, stepIndex, approverIndex, { approvalType: value })}
                                                        />

                                                        {!disabled && step.approvers.length > 1 && (
                                                            <Button variant="ghost" className="p-2 text-red-400" onClick={() => removeApprover(tierIndex, stepIndex, approverIndex)} title="Remove approver">
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                    );
                                                })}
                                                {!disabled && (
                                                    <button
                                                        type="button"
                                                        onClick={() => addApprover(tierIndex, stepIndex)}
                                                        className="text-sm text-yavrio-blue hover:underline flex items-center gap-1 w-fit mt-1"
                                                    >
                                                        <Plus className="w-3.5 h-3.5" /> Add approver
                                                    </button>
                                                )}
                                            </div>
                                            </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {!disabled && (
                                    <Button variant="outline" className="gap-2 w-fit" onClick={() => addStep(tierIndex)}>
                                        <Plus className="w-4 h-4" /> Add ordered step
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                        )}
                    </Card>
                );
            })}

            {!disabled && (
                <Button variant="outline" className="gap-2 w-fit" onClick={addTier}>
                    <Plus className="w-4 h-4" /> Add value tier
                </Button>
            )}
        </div>
        </TooltipProvider>
    );
}
